import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, FileText, Image as ImageIcon, Check } from 'lucide-react';
import { API_BASE as API } from '../../config';

/** Bottom-sheet drawer that lists every file currently uploaded against a
 *  claim and lets the user pick one or more to attach to an outgoing email.
 *  Each file becomes an AttachmentRef with just a `key` (GridFS file_id) +
 *  `name` — the backend rehydrates the bytes at send time. */
const FilePickerDrawer = ({ open, onClose, reportNo, onPick, alreadyPickedKeys = [] }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (!open || !reportNo) return;
    setLoading(true);
    setSelected({});
    axios
      .get(`${API}/claim-docs/files/${encodeURIComponent(reportNo)}`)
      .then((r) => setFiles(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFiles([]))
      .finally(() => setLoading(false));
  }, [open, reportNo]);

  if (!open) return null;

  const isImage = (name) => /\.(png|jpe?g|gif|webp|heic|heif|bmp|svg)$/i.test(name || '');
  const isPdf = (name) => /\.pdf$/i.test(name || '');

  const toggle = (f) => {
    if (alreadyPickedKeys.includes(f.key)) return; // already on email
    setSelected((prev) => ({ ...prev, [f.key]: prev[f.key] ? undefined : f }));
  };

  const attachSelected = () => {
    const picked = Object.values(selected).filter(Boolean);
    if (!picked.length) return;
    onPick(picked.map((f) => ({ name: f.name, key: f.key, content_type: f.content_type })));
    onClose?.();
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/50"
      onClick={onClose}
      data-testid="file-picker-drawer"
    >
      <div
        className="bg-white w-full md:w-[640px] md:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Attach from claim</h3>
            <p className="text-xs text-gray-500 mt-0.5">Pick one or more uploaded files</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100" data-testid="file-picker-close">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <p className="text-center text-sm text-gray-400 py-12">Loading files…</p>
          ) : files.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-12">No files uploaded to this claim yet.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {files.map((f) => {
                const sel = !!selected[f.key];
                const used = alreadyPickedKeys.includes(f.key);
                return (
                  <button
                    key={f.key || f.url}
                    onClick={() => toggle(f)}
                    disabled={used}
                    className={`relative group border-2 rounded-lg p-2 text-left transition-all ${
                      used
                        ? 'border-gray-200 opacity-40 cursor-not-allowed'
                        : sel
                          ? 'border-teal-500 bg-teal-50/60'
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`file-pick-${f.name}`}
                    title={used ? 'Already attached' : f.name}
                  >
                    <div className="aspect-square bg-gray-50 rounded-md flex items-center justify-center overflow-hidden mb-2">
                      {isImage(f.name) ? (
                        <img src={f.url} alt={f.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : isPdf(f.name) ? (
                        <FileText className="w-10 h-10 text-red-400" />
                      ) : (
                        <FileText className="w-10 h-10 text-gray-400" />
                      )}
                    </div>
                    <p className="text-[11px] text-gray-700 truncate" title={f.name}>{f.name}</p>
                    {sel && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center shadow-sm">
                        <Check className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    {used && (
                      <div className="absolute top-1 right-1 text-[9px] uppercase tracking-wide bg-gray-700 text-white px-1.5 py-0.5 rounded">
                        Attached
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-500">{selectedCount} selected</span>
          <button
            onClick={attachSelected}
            disabled={selectedCount === 0}
            className="px-4 py-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded transition-colors"
            data-testid="file-picker-attach"
          >
            Attach {selectedCount > 0 ? `(${selectedCount})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilePickerDrawer;
